import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import ResearchReportView, { StatusBadge } from "../../components/app/ResearchReportView";
import ResearchSectionsView, { ExportButton }
    from "../../components/app/ResearchSectionsView";

export default function ResearchDetail() {
  const { id } = useParams();
  const t = useT();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/research/reports/${id}`);
      setReport(r.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Not found");
        navigate("/app/research");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const retry = async () => {
    setRetrying(true);
    try {
      await api.post(`/research/reports/${id}/retry`);
      toast.success(t("research.retry_success"));
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(t("research.error"));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div data-testid="research-detail-page" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm" data-testid="back-to-list">
          <Link to="/app/research"><ArrowLeft className="size-4 mr-1" /> {t("research.back_to_list")}</Link>
        </Button>
        {report?.status === "failed" && (
          <Button onClick={retry} disabled={retrying} variant="outline" size="sm" data-testid="detail-retry">
            <RefreshCw className="size-4 mr-1" />
            {retrying ? t("research.retrying") : t("research.retry")}
          </Button>
        )}
      </div>

      {report && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <StatusBadge status={report.status} />
            <span className="text-xs text-muted-foreground font-mono">{report.id}</span>
          </div>
          {report.sections && <ExportButton reportId={report.id} />}
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : report ? (
        report.sections && Object.keys(report.sections).length > 0 ? (
          <ResearchSectionsView
            report={report}
            onSectionUpdated={(key, newVal, used) => {
              setReport({
                ...report,
                sections: { ...report.sections, [key]: newVal },
                section_regenerations_used: {
                  ...(report.section_regenerations_used || {}),
                  [key]: used,
                },
              });
            }}
          />
        ) : (
          <ResearchReportView report={report} />
        )
      ) : null}
    </div>
  );
}
